import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdDashboard, MdBuild, MdDevices, MdShoppingCart, MdForum, MdTask, MdHelpOutline, MdWavingHand } from 'react-icons/md';
import { useLanguage } from '../i18n';
import { useAuth } from '../hooks/useAuth';

// A short first-visit tour (once per person per browser). It can be reopened
// from the Help panel at any time.
export default function WelcomeTour() {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const { user, isEmployee, isDeveloper } = useAuth();
    const doneKey = `fms_tour_done_${user.id || 'x'}`;
    const [open, setOpen] = useState(false);
    const [index, setIndex] = useState(0);

    const steps = isEmployee ? [
        { icon: <MdWavingHand />, title: t('tour.welcomeTitle', '', { name: user.name || '' }), text: t('tour.welcomeText') },
        { icon: <MdTask />, title: t('tour.myTasksTitle'), text: t('tour.myTasksText'), to: '/app/my-tasks' },
        { icon: <MdShoppingCart />, title: t('tour.requestsTitle'), text: t('tour.requestsText'), to: '/app/requests' },
        { icon: <MdForum />, title: t('tour.messagesTitle'), text: t('tour.messagesText'), to: '/app/messages' },
        { icon: <MdHelpOutline />, title: t('tour.helpTitle'), text: t('tour.helpText') },
    ] : [
        { icon: <MdWavingHand />, title: t('tour.welcomeTitle', '', { name: user.name || '' }), text: t('tour.welcomeText') },
        { icon: <MdDashboard />, title: t('tour.dashboardTitle'), text: t('tour.dashboardText'), to: '/app/dashboard' },
        { icon: <MdBuild />, title: t('tour.workTitle'), text: t('tour.workText'), to: '/app/work-orders' },
        { icon: <MdDevices />, title: t('tour.stockTitle'), text: t('tour.stockText'), to: '/app/assets' },
        { icon: <MdShoppingCart />, title: t('tour.requestsTitle'), text: t('tour.requestsText'), to: '/app/requests' },
        { icon: <MdForum />, title: t('tour.messagesTitle'), text: t('tour.messagesText'), to: '/app/messages' },
        { icon: <MdHelpOutline />, title: t('tour.helpTitle'), text: t('tour.helpText') },
    ];

    const finish = useCallback(() => {
        try { localStorage.setItem(doneKey, '1'); } catch { /* private mode */ }
        setOpen(false);
        setIndex(0);
    }, [doneKey]);

    // First visit: open automatically (never for the platform developer account).
    useEffect(() => {
        if (isDeveloper) return undefined;
        let seen = true;
        try { seen = !!localStorage.getItem(doneKey); } catch { /* ignore */ }
        if (seen) return undefined;
        const timer = window.setTimeout(() => setOpen(true), 900);
        return () => window.clearTimeout(timer);
    }, [doneKey, isDeveloper]);

    // Reopened from the Help panel.
    useEffect(() => {
        const start = () => { setIndex(0); setOpen(true); };
        window.addEventListener('fms:start-tour', start);
        return () => window.removeEventListener('fms:start-tour', start);
    }, []);

    if (!open) return null;
    const step = steps[index];
    const last = index === steps.length - 1;
    const go = delta => {
        const next = index + delta;
        if (next < 0 || next >= steps.length) return;
        setIndex(next);
        if (steps[next].to) navigate(steps[next].to);
    };

    return (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 430, padding: '28px 26px 22px', boxShadow: '0 24px 60px rgba(0,0,0,.35)', textAlign: 'center' }}>
                <div style={{ width: 64, height: 64, borderRadius: 18, background: 'linear-gradient(135deg,#0ea5e9,#2563eb)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, margin: '0 auto 16px' }}>{step.icon}</div>
                <h5 style={{ fontWeight: 800, marginBottom: 8 }}>{step.title}</h5>
                <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.6, minHeight: 72 }}>{step.text}</p>

                <div style={{ display: 'flex', justifyContent: 'center', gap: 6, margin: '10px 0 4px' }}>
                    {steps.map((_, i) => <span key={i} style={{ width: i === index ? 22 : 8, height: 8, borderRadius: 8, background: i === index ? '#2563eb' : '#cbd5e1', transition: 'width .2s' }} />)}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>{t('tour.stepOf', '', { n: index + 1, total: steps.length })}</div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button type="button" className="btn btn-link btn-sm" style={{ color: '#64748b', textDecoration: 'none' }} onClick={finish}>{t('tour.skip')}</button>
                    <span style={{ flex: 1 }} />
                    {index > 0 && <button type="button" className="btn btn-light" onClick={() => go(-1)}>{t('tour.back')}</button>}
                    {last
                        ? <button type="button" className="btn btn-primary" onClick={finish}>{t('tour.finish')}</button>
                        : <button type="button" className="btn btn-primary" onClick={() => go(1)}>{t('tour.next')}</button>}
                </div>
            </div>
        </div>
    );
}
